import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import PriceTable from './PriceTable';
import { Margin } from '~lib/frontend/design-system/components/atoms/utils';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '~lib/frontend/design-system/components/tabs';
import useContentKey from '~lib/frontend/hooks/use-content-key';
import { GetterType } from '~lib/frontend/types/content';
import { trackQuoteEvent } from '~website/features/analytics/store/actions';

type TabsContent = Record<string, TabContent>;
interface TabContent {
	title: string;
	options: PriceOption[];
}
interface PriceOption {
	duration: string;
	amount: string;
}

const fallbackTabs: TabsContent = {
	hours: {
		title: 'Hours',
		options: [
			{ duration: '1 hour', amount: '£12.03' },
			{ duration: '2 hours', amount: '£12.62' },
			{ duration: '3 hours', amount: '£12.68' },
			{ duration: '6 hours', amount: '£13.88' },
		],
	},
	days: {
		title: 'Days',
		options: [
			{ duration: '1 day', amount: '£17.32' },
			{ duration: '2 days', amount: '£22.36' },
			{ duration: '3 days', amount: '£27.17' },
			{ duration: '4 days', amount: '£31.46' },
		],
	},
	weeks: {
		title: 'Weeks',
		options: [
			{ duration: '1 week', amount: '£44.40' },
			{ duration: '2 weeks', amount: '£70.84' },
			{ duration: '3 weeks', amount: '£91.77' },
			{ duration: '4 weeks', amount: '£139.31' },
		],
	},
};

const isValidTabsContent = (tabs: unknown): tabs is TabsContent => {
	if (!tabs || typeof tabs !== 'object') return false;

	return Object.values(tabs).every(
		tab =>
			tab.title &&
			Array.isArray(tab.options) &&
			tab.options.every((option: PriceOption) => option.duration && option.amount)
	);
};

const PriceTabs: React.FC = () => {
	const dispatch = useDispatch();
	const g = useContentKey('strings');
	const get: GetterType = (key, fallback) => g(`prelogin.indicative_price.${key}`, fallback);

	const rawTabs = get('tabs', fallbackTabs);
	const tabs = isValidTabsContent(rawTabs) ? rawTabs : fallbackTabs;
	const tabKeys = Object.keys(tabs);

	const handleTabChange = (index: number) => {
		const tabKey = tabKeys[index];
		const tabTitle = tabs[tabKey].title.toLowerCase();

		dispatch(trackQuoteEvent({ action: `sample_prices_${tabTitle}_tab_selected` }));
	};

	return (
		<Tabs onChange={handleTabChange}>
			<TabList>
				{tabKeys.map(key => (
					<StyledTab key={key}>{tabs[key].title}</StyledTab>
				))}
			</TabList>

			<Margin $marginTop={'extraLarge'} />
			<TabPanels>
				{tabKeys.map(key => (
					<TabPanel key={key}>
						<PriceTable items={tabs[key].options} />
					</TabPanel>
				))}
			</TabPanels>
		</Tabs>
	);
};

const StyledTab = styled(Tab)`
	flex: 1;
	display: flex;
	justify-content: center;
`;

export default PriceTabs;
